// studioTools — the Studio toolbar's tools and its single-letter keys (pen
// B4). Pure. The active tool is view-ephemeral (it never survives a reload).
//
// Keys (owner, 2026-10-20): V H C S M F A and Esc work only while the canvas has
// focus and no text field is focused. Esc drops a tool or gesture in
// progress, else clears the selection; ↑ / ↓ during a combining drop switch
// the new container's kind. A key with a modifier is never ours
// (web chords belong to the browser), so it maps to nothing and the caller
// leaves it alone.

export type { KeyPress, Tool, ToolState };
export { keyIntent };

// A mode the canvas stays in until another is chosen. Source and Material are
// one-shot actions, not modes.
type Tool = "select" | "hand" | "container" | "assistant";

type ToolKey =
    | { readonly type: "pick"; readonly tool: Tool }
    // C with cards selected: put them in a new module at once.
    | { readonly type: "wrap-selection" }
    | { readonly type: "upload" }
    | { readonly type: "open-material-menu" }
    // F: find on this desk.
    | { readonly type: "find" }
    // Esc: drop a tool in progress and go back to Select.
    | { readonly type: "cancel" }
    // Esc with nothing in progress: clear the selection.
    | { readonly type: "clear-selection" }
    // ↑ / ↓ while a dragged card is over one it would combine with: the
    // other kind of new container (module ↔ course).
    | { readonly type: "switch-kind" };

type KeyPress = {
    readonly key: string;
    readonly modified: boolean;
    readonly inTextField: boolean;
};

type ToolState = {
    readonly tool: Tool;
    readonly gestureActive: boolean;
    readonly selectedCount: number;
    readonly online: boolean;
    // A dragged card is over one it would combine with into a new container.
    readonly combining: boolean;
};

const keyIntent = (press: KeyPress, state: ToolState): ToolKey | null => {
    if (press.modified || press.inTextField) return null;
    switch (press.key.toLowerCase()) {
        case "v":
            return { type: "pick", tool: "select" };
        case "h":
            return { type: "pick", tool: "hand" };
        case "c":
            if (!state.online) return null;
            return state.selectedCount > 0
                ? { type: "wrap-selection" }
                : { type: "pick", tool: "container" };
        case "s":
            return state.online ? { type: "upload" } : null;
        case "m":
            return state.online ? { type: "open-material-menu" } : null;
        case "a":
            return state.online ? { type: "pick", tool: "assistant" } : null;
        case "f":
            return { type: "find" };
        case "escape":
            return state.tool !== "select" || state.gestureActive
                ? { type: "cancel" }
                : state.selectedCount > 0
                  ? { type: "clear-selection" }
                  : null;
        case "arrowup":
        case "arrowdown":
            return state.combining ? { type: "switch-kind" } : null;
        default:
            return null;
    }
};
